// src/services/notifications/push.js
// FCM push notification client.
// Covers: permission, token lifecycle, topic subscriptions,
// foreground message handling, and offline queue.

import { FEATURES }       from '@config';
import { nearbyParishes } from '@services/geolocation';

// ── Permission status enum ────────────────────────────────────────
export const PUSH_STATUS = {
  GRANTED:     'granted',
  DENIED:      'denied',
  DEFAULT:     'default',       // not yet asked
  UNSUPPORTED: 'unsupported',
};

export function getPushPermissionStatus() {
  if (!('Notification' in window) || !('PushManager' in window)) {
    return PUSH_STATUS.UNSUPPORTED;
  }
  return Notification.permission; // 'granted' | 'denied' | 'default'
}

export async function requestPushPermission() {
  if (!('Notification' in window)) return PUSH_STATUS.UNSUPPORTED;
  if (Notification.permission !== 'default') return Notification.permission;
  const result = await Notification.requestPermission();
  return result; // 'granted' | 'denied'
}

// ── Topic maps ────────────────────────────────────────────────────
export const PARISH_TOPICS = {
  'N.O. East':            'parish-no-east',
  'Lakeview':             'parish-lakeview',
  'Gentilly':             'parish-gentilly',
  'Lower 9th Ward':       'parish-lower-9th',
  'Carrollton':           'parish-carrollton',
  'Uptown':               'parish-uptown',
  'Mid-City':             'parish-mid-city',
  'French Quarter':       'parish-french-quarter',
  'Algiers':              'parish-algiers',
  'Northshore / Slidell': 'parish-northshore',
  'Orleans':              'parish-orleans',
  'Jefferson':            'parish-jefferson',
  'St. Bernard':          'parish-st-bernard',
  'Plaquemines':          'parish-plaquemines',
  'St. Tammany':          'parish-st-tammany',
  'St. Charles':          'parish-st-charles',
  'St. John':             'parish-st-john',
};

export const ALERT_TYPE_TOPICS = {
  nws_extreme:  'alert-nws-extreme',
  nws_severe:   'alert-nws-severe',
  flood_warning:'alert-flood-warning',
  evacuation:   'alert-evacuation',
  sos_nearby:   'alert-sos-nearby',
  power:        'alert-power',
  road:         'alert-road',
  shelter:      'alert-shelter',
  curfew:       'alert-curfew',
};

// Mandatory — cannot be unsubscribed regardless of user prefs.
// Life-safety topics that must always reach every installed user.
export const MANDATORY_TOPICS = ['severity-extreme', 'alert-evacuation'];

// ── Storage keys ──────────────────────────────────────────────────
const KEY_TOKEN    = 'na-fcm-token';
const KEY_TOKEN_TS = 'na-fcm-token-ts';
const KEY_TOPICS   = 'na-subscribed-topics';
const KEY_QUEUE    = 'na-topic-queue';
const TOKEN_TTL    = 7 * 24 * 60 * 60_000;  // 7 days

// ── FCM singleton ─────────────────────────────────────────────────
let _messaging = null;

async function _getMessaging() {
  if (_messaging) return _messaging;
  if (!FEATURES.firebase || !FEATURES.pushNotifications) return null;
  const { getApps }                     = await import('firebase/app');
  const { getMessaging: _get, isSupported } = await import('firebase/messaging');
  if (!await isSupported().catch(() => false)) return null;
  const app = getApps()[0];
  if (!app) return null;
  _messaging = _get(app);
  return _messaging;
}

// ── FCM Token ─────────────────────────────────────────────────────
export async function getFCMToken(swRegistration) {
  const VAPID = import.meta.env.VITE_FIREBASE_VAPID_KEY;
  if (!VAPID) { console.warn('[Push] VITE_FIREBASE_VAPID_KEY not set'); return null; }
  const messaging = await _getMessaging();
  if (!messaging) return null;
  const { getToken } = await import('firebase/messaging');
  try {
    const token = await getToken(messaging, {
      vapidKey: VAPID, serviceWorkerRegistration: swRegistration,
    });
    if (token) {
      localStorage.setItem(KEY_TOKEN,    token);
      localStorage.setItem(KEY_TOKEN_TS, String(Date.now()));
    }
    return token ?? null;
  } catch (err) {
    console.error('[Push] getToken failed:', err.message);
    return null;
  }
}

export async function refreshTokenIfStale(swReg) {
  const ts = parseInt(localStorage.getItem(KEY_TOKEN_TS) ?? '0', 10);
  if (Date.now() - ts < TOKEN_TTL) return localStorage.getItem(KEY_TOKEN);
  return getFCMToken(swReg);
}

export const getStoredToken = () => localStorage.getItem(KEY_TOKEN);

// ── Foreground message handler ────────────────────────────────────
// When the app is in the foreground, FCM doesn't show the system notification.
// We display an in-app banner and fire DOM events for the hook to handle.
// Call this once after FCM is initialized.
export async function initForegroundMessages() {
  const messaging = await _getMessaging();
  if (!messaging) return;
  const { onMessage } = await import('firebase/messaging');

  onMessage(messaging, (payload) => {
    const data = payload.data ?? {};
    const notification = payload.notification ?? {};

    const detail = {
      title:     notification.title ?? 'Eye on Storm',
      body:      notification.body  ?? '',
      alertId:   data.alertId   ?? null,
      alertType: data.alertType ?? 'general',
      severity:  data.severity  ?? 'moderate',
      url:       data.url       ?? '/',
      issuedAt:  data.issuedAt  ?? new Date().toISOString(),
    };

    // All foreground messages fire this event.
    // The usePushNotifications hook listens and shows an in-app banner.
    window.dispatchEvent(new CustomEvent('eos-foreground-message', { detail }));

    // Life-safety events also fire the emergency event for the modal.
    const isEmergency = detail.severity === 'extreme'
      || detail.alertType === 'evacuation'
      || detail.alertType === 'sos_nearby';

    if (isEmergency) {
      window.dispatchEvent(new CustomEvent('na-emergency-alert', { detail }));
      navigator.vibrate?.([500, 200, 500, 200, 500, 200, 1000]);
    }
  });
}

// ── Topic list builder ────────────────────────────────────────────
// Accepts either `zone` (single, backward compat) or `zones` (array).
// All selected zones get their own parish FCM topic.
export function buildTopicList({ zone, zones, alertTypePrefs = {} }) {
  const topics = new Set(MANDATORY_TOPICS);

  // Subscribe to all selected zones — the user gets alerts for any of them
  const activeZones = zones?.length > 0 ? zones : (zone ? [zone] : []);
  for (const z of activeZones) {
    if (PARISH_TOPICS[z]) topics.add(PARISH_TOPICS[z]);
  }

  for (const [type, enabled] of Object.entries(alertTypePrefs)) {
    if (enabled && ALERT_TYPE_TOPICS[type]) topics.add(ALERT_TYPE_TOPICS[type]);
  }
  return [...topics];
}

// ── Subscription API ──────────────────────────────────────────────
const FUNCTIONS_BASE = import.meta.env.VITE_FUNCTIONS_URL
  ?? 'https://us-central1-eyeonstorm-e7bff.cloudfunctions.net';

async function _callTopicAPI(action, token, topics) {
  // Get Firebase Auth token for manageTopics (now requires auth)
  let _authHeader = '';
  try {
    const { getAuth } = await import('firebase/auth');
    const _user = getAuth().currentUser;
    if (_user) _authHeader = await _user.getIdToken();
  } catch {}
  const res = await fetch(`${FUNCTIONS_BASE}/manageTopics`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json', ...(_authHeader ? { Authorization: 'Bearer ' + _authHeader } : {}) },
    body:    JSON.stringify({ action, token, topics }),
    signal:  AbortSignal.timeout(10_000),
  });
  if (!res.ok) throw new Error(`manageTopics ${res.status}`);
  return res.json();
}

export async function subscribeToTopics(token, topics) {
  return _callTopicAPI('subscribe', token, topics);
}

export async function unsubscribeFromTopics(token, topics) {
  const safe = topics.filter((t) => !MANDATORY_TOPICS.includes(t));
  if (!safe.length) return;
  return _callTopicAPI('unsubscribe', token, safe);
}

// ── Full setup ────────────────────────────────────────────────────
export async function setupPushNotifications({ zone, zones, alertTypePrefs, swRegistration }) {
  if (!('Notification' in window) || !('PushManager' in window)) {
    return { ok: false, reason: PUSH_STATUS.UNSUPPORTED };
  }

  const perm = Notification.permission === 'default'
    ? await requestPushPermission()
    : Notification.permission;
  if (perm !== 'granted') return { ok: false, reason: perm };

  const token = await getFCMToken(swRegistration);
  if (!token) return { ok: false, reason: 'no_token' };

  // Start handling foreground messages now that we have a token
  initForegroundMessages();

  // Initialize FCM background messaging in the SW.
  // Must happen after we have a valid token — this posts the Firebase
  // config to the SW so it can handle push messages when app is closed.
  initFCMInSW(swRegistration).catch(() => {});

  const topics = buildTopicList({ zone, zones, alertTypePrefs });

  // Wait for Firebase Auth to resolve before calling manageTopics
  // manageTopics now requires a Firebase Auth ID token
  let _authReady = false;
  try {
    const { getAuth } = await import('firebase/auth');
    const _auth = getAuth();
    // If user is already signed in, proceed immediately
    if (_auth.currentUser) {
      _authReady = true;
    } else {
      // Wait up to 5 seconds for auth state to resolve
      _authReady = await new Promise((resolve) => {
        const unsub = _auth.onAuthStateChanged((user) => {
          unsub();
          resolve(!!user);
        });
        setTimeout(() => { unsub(); resolve(false); }, 5000);
      });
    }
  } catch { _authReady = false; }

  if (!_authReady) {
    // Not signed in — queue subscription for later
    _queueSubscription({ token, topics });
    return { ok: true, token, topics, queued: true };
  }

  try {
    await subscribeToTopics(token, topics);
    _saveTopics(topics);
    return { ok: true, token, topics };
  } catch {
    _queueSubscription({ token, topics });
    return { ok: true, token, topics, queued: true };
  }
}

// ── Update subscriptions on pref change ──────────────────────────
export async function updateTopicSubscriptions({ zone, newPrefs, oldPrefs }) {
  const token = getStoredToken();
  if (!token) return;
  const oldTopics = buildTopicList({ zone, alertTypePrefs: oldPrefs });
  const newTopics = buildTopicList({ zone, alertTypePrefs: newPrefs });
  const toAdd    = newTopics.filter((t) => !oldTopics.includes(t));
  const toRemove = oldTopics.filter((t) => !newTopics.includes(t));
  try {
    await Promise.all([
      toAdd.length    ? subscribeToTopics(token, toAdd)         : null,
      toRemove.length ? unsubscribeFromTopics(token, toRemove)  : null,
    ].filter(Boolean));
    _saveTopics(newTopics);
  } catch {
    _queueSubscription({ token, topics: toAdd, toRemove });
  }
}

// ── Geofenced parish subscription ────────────────────────────────
export async function subscribeToNearbyParishes(lat, lon, radiusKm = 25) {
  const token = getStoredToken();
  if (!token) return [];
  const parishes = nearbyParishes(lat, lon, radiusKm);
  const topics   = parishes.map((p) => PARISH_TOPICS[p]).filter(Boolean);
  if (!topics.length) return [];
  try {
    await subscribeToTopics(token, topics);
    _saveTopics([...new Set([...getSubscribedTopics(), ...topics])]);
  } catch {
    _queueSubscription({ token, topics });
  }
  return topics;
}

// ── Offline subscription queue ────────────────────────────────────
function _queueSubscription(item) {
  try {
    const q = JSON.parse(localStorage.getItem(KEY_QUEUE) ?? '[]');
    q.push({ ...item, ts: Date.now() });
    localStorage.setItem(KEY_QUEUE, JSON.stringify(q));
  } catch {}
}

export async function flushTopicQueue() {
  try {
    const queue = JSON.parse(localStorage.getItem(KEY_QUEUE) ?? '[]');
    if (!queue.length) return 0;

    // Require Firebase Auth before flushing — manageTopics needs ID token
    let _authed = false;
    try {
      const { getAuth } = await import('firebase/auth');
      _authed = !!getAuth().currentUser;
    } catch {}
    if (!_authed) return 0; // Not signed in yet — leave queue intact

    let flushed = 0;
    const remaining = [];
    for (const item of queue) {
      try {
        if (item.topics?.length)   await subscribeToTopics(item.token, item.topics);
        if (item.toRemove?.length) await unsubscribeFromTopics(item.token, item.toRemove);
        flushed++;
      } catch { remaining.push(item); }
    }
    localStorage.setItem(KEY_QUEUE, JSON.stringify(remaining));
    return flushed;
  } catch { return 0; }
}

if (typeof window !== 'undefined') {
  // Only flush when online AND auth is resolved
  window.addEventListener('online', () => {
    // Small delay to let auth state settle after reconnect
    setTimeout(() => flushTopicQueue(), 2000);
  });
}

// ── Topic persistence ─────────────────────────────────────────────
const _saveTopics    = (t) => { try { localStorage.setItem(KEY_TOPICS, JSON.stringify(t)); } catch {} };
export const getSubscribedTopics = () => { try { return JSON.parse(localStorage.getItem(KEY_TOPICS) ?? '[]'); } catch { return []; } };

// ── Background sync registration ─────────────────────────────────
export function registerBackgroundSync(swReg, tag = 'na-sync') {
  swReg?.sync?.register(tag).catch(() => {});
}

// ── Initialize FCM in the service worker ─────────────────────────
// The SW cannot read import.meta.env, so it cannot initialize Firebase
// on its own. We post the Firebase config + VAPID key to the SW via
// a message channel once the app starts and has the config available.
//
// The SW listens for FCM_CONFIG messages (see sw.js _initFCM) and
// calls importScripts + firebase.messaging().onBackgroundMessage().
//
// This must be called once after the SW is registered and Firebase
// is initialized. setupPushNotifications() calls it automatically.
export async function initFCMInSW(swRegistration) {
  if (!swRegistration) return;
  const { FIREBASE } = await import('@config');
  const VAPID        = import.meta.env.VITE_FIREBASE_VAPID_KEY;
  if (!FIREBASE || !VAPID) return;

  try {
    const sw = swRegistration.active
            ?? swRegistration.waiting
            ?? swRegistration.installing;
    if (!sw) return;
    sw.postMessage({ type: 'FCM_CONFIG', config: FIREBASE, vapidKey: VAPID });
  } catch (err) {
    console.warn('[Push] initFCMInSW failed (non-fatal):', err.message);
  }
}
